import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

export interface RealtimeTopic {
  id: string
  url: string
  invalidates: ReadonlyArray<ReadonlyArray<unknown>>
  throttleMs?: number
}

type Entry = {
  invalidates: ReadonlyArray<ReadonlyArray<unknown>>
  throttleMs: number
  refCount: number
  es: EventSource | null
  lastFire: number
  pending: ReturnType<typeof setTimeout> | null
}

type HubApi = {
  subscribe: (topic: RealtimeTopic) => () => void
}

const HubContext = createContext<HubApi | null>(null)

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const entries = useRef<Map<string, Entry>>(new Map())

  const invalidateAll = useCallback(
    (invalidates: ReadonlyArray<ReadonlyArray<unknown>>) => {
      invalidates.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key as unknown[] })
      })
    },
    [queryClient],
  )

  const subscribe = useCallback<HubApi['subscribe']>(
    (topic) => {
      const existing = entries.current.get(topic.id)
      if (existing) {
        existing.refCount += 1
        existing.invalidates = topic.invalidates
        existing.throttleMs = topic.throttleMs ?? existing.throttleMs
      } else {
        const entry: Entry = {
          invalidates: topic.invalidates,
          throttleMs: topic.throttleMs ?? 1000,
          refCount: 1,
          es: null,
          lastFire: 0,
          pending: null,
        }
        entries.current.set(topic.id, entry)
        const es = new EventSource(topic.url, { withCredentials: true })
        es.onmessage = () => {
          const entryNow = entries.current.get(topic.id)
          if (!entryNow) return
          const now = Date.now()
          const elapsed = now - entryNow.lastFire
          if (elapsed >= entryNow.throttleMs) {
            entryNow.lastFire = now
            invalidateAll(entryNow.invalidates)
            return
          }
          if (!entryNow.pending) {
            entryNow.pending = setTimeout(
              () => {
                entryNow.pending = null
                entryNow.lastFire = Date.now()
                invalidateAll(entryNow.invalidates)
              },
              entryNow.throttleMs - elapsed,
            )
          }
        }
        es.onerror = () => {
          if (process.env.NODE_ENV !== 'production') {
            console.debug('RealtimeHub: stream error, browser will reconnect', topic.id)
          }
        }
        entry.es = es
      }
      return () => {
        const entry = entries.current.get(topic.id)
        if (!entry) return
        entry.refCount -= 1
        if (entry.refCount > 0) return
        // Grace window so StrictMode double-mount and fast route swaps don't
        // close+reopen the EventSource.
        setTimeout(() => {
          const stillIdle = entries.current.get(topic.id)
          if (!stillIdle || stillIdle.refCount > 0) return
          if (stillIdle.pending) clearTimeout(stillIdle.pending)
          stillIdle.es?.close()
          entries.current.delete(topic.id)
        }, 50)
      }
    },
    [invalidateAll],
  )

  // Without memoization, unrelated AppLayout re-renders would tear down
  // every subscriber's EventSource.
  const value = useMemo<HubApi>(() => ({ subscribe }), [subscribe])
  return <HubContext.Provider value={value}>{children}</HubContext.Provider>
}

export function useRealtimeTopic(
  topic: RealtimeTopic | null,
  enabled: boolean = true,
): void {
  const ctx = useContext(HubContext)
  if (!ctx) {
    throw new Error('useRealtimeTopic must be used inside <RealtimeProvider>')
  }
  const invalidatesRef = useRef(topic?.invalidates)
  invalidatesRef.current = topic?.invalidates

  useEffect(() => {
    if (!enabled || !topic) return
    return ctx.subscribe({
      id: topic.id,
      url: topic.url,
      get invalidates() {
        return invalidatesRef.current ?? []
      },
      throttleMs: topic.throttleMs,
    } as RealtimeTopic)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, topic?.id, topic?.url, enabled])
}
